/**
 * Ryan O'Dowd
 * 2025-02-09
 * © Copyright 2025 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useEffect,
  useState,
} from 'react';
import {
  fetchNotificationPreferences,
  updateNotificationPreferences,
} from '@utils/store/actions';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import ScreenWrapper from '@containers/ScreenWrapper';
import Text from '@components/common/Text';
import {
  TimePickerModal,
} from 'react-native-paper-dates';
import {
  TouchableOpacity,
} from 'react-native';
import TypeItem from './TypeItem';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import hooks from '@hooks';

const Notifications = () => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);

  hooks.useBackButton(() => {
    navigation.goBack();
    return true;
  });

  useEffect(() => {
    dispatch(fetchNotificationPreferences());
  }, [dispatch]);

  const [_isStreakTimePickerVisible, setIsStreakTimePickerVisible] = useState(false);
  const [_isPassageOfTheDayTimePickerVisible, setIsPassageOfTheDayTimePickerVisible] = useState(false);

  // @TODO: const betaOn = hooks.useTypedSelector((state) => state.userSettings.beta_on);
  const notificationPreferences = hooks.useTypedSelector((state) => state.notificationPreferences);

  const backIcon = <Header.BackIcon />;

  const streakInitialTimeDateObject = new Date();
  const [streakHours, streakMinutes] = notificationPreferences.streak_reminder_time.split(':');
  streakInitialTimeDateObject.setHours(streakHours);
  streakInitialTimeDateObject.setMinutes(streakMinutes);

  const passageOfTheDayInitialTimeDateObject = new Date();
  const [passageOfTheDayHours, passageOfTheDayMinutes] = notificationPreferences.passage_of_the_day_time.split(':');
  passageOfTheDayInitialTimeDateObject.setHours(passageOfTheDayHours);
  passageOfTheDayInitialTimeDateObject.setMinutes(passageOfTheDayMinutes);

  return (
    <ScreenWrapper>
      <Header
        leftItems={[backIcon]}
        title={i18n('notifications')}
      />
      <FullScreenScrollView
        contentContainerStyle={styles.list}
        showsVerticalScrollIndicator={false}
      >
        {/* @TODO: text field for updating email address for notifications; does this need to be verified? */}

        <TypeItem
          title={i18n('streakReminders')}
          description={i18n('streakRemindersDescription')}
          pushStatus={!!notificationPreferences.streak_reminder_channels.push}
          emailStatus={!!notificationPreferences.streak_reminder_channels.email}
          pushToggle={() => {
            const currOn = notificationPreferences.streak_reminder_channels.push;
            dispatch(updateNotificationPreferences({streak_reminder_on_push: +!currOn}));
          }}
          emailToggle={() => {
            const currOn = notificationPreferences.streak_reminder_channels.email;
            dispatch(updateNotificationPreferences({streak_reminder_on_email: +!currOn}));
          }}
        >
          <TouchableOpacity style={styles.reminderTextWrapper}>
            {/* @TODO: time selection, etc. */}
            <Text style={styles.timeText}>{i18n('remindersWillBeSentAt')} </Text>
            <TouchableOpacity style={styles.timeDisplay} onPress={() => setIsStreakTimePickerVisible(true)}><Text style={[styles.timeText, styles.time]}>{dateFnsFormat(streakInitialTimeDateObject, "h:mmaaaaa'm'", {locale: i18n.dateFnsLocale})}</Text></TouchableOpacity>
            <TimePickerModal
              visible={_isStreakTimePickerVisible}
              hours={streakHours}
              minutes={streakMinutes}
              onConfirm={({hours, minutes}) => {
                dispatch(updateNotificationPreferences({streak_reminder_time: `${hours}:${minutes}`}));
                setIsStreakTimePickerVisible(false);
              }}
              onDismiss={() => setIsStreakTimePickerVisible(false)}
            />
          </TouchableOpacity>
        </TypeItem>

        <TypeItem
          title={i18n('passageOfTheDay')}
          description={i18n('passageOfTheDayDescription')}
          pushStatus={!!notificationPreferences.passage_of_the_day_channels.push}
          emailStatus={!!notificationPreferences.passage_of_the_day_channels.email}
          pushToggle={() => {
            const currOn = notificationPreferences.passage_of_the_day_channels.push;
            dispatch(updateNotificationPreferences({passage_of_the_day_on_push: +!currOn}));
          }}
          emailToggle={() => {
            const currOn = notificationPreferences.passage_of_the_day_channels.email;
            dispatch(updateNotificationPreferences({passage_of_the_day_on_email: +!currOn}));
          }}
        >
          <TouchableOpacity style={styles.reminderTextWrapper}>
            <Text style={styles.timeText}>{i18n('notificationsWillBeSentAt')} </Text>
            <TouchableOpacity style={styles.timeDisplay} onPress={() => setIsPassageOfTheDayTimePickerVisible(true)}><Text style={[styles.timeText, styles.time]}>{dateFnsFormat(passageOfTheDayInitialTimeDateObject, "h:mmaaaaa'm'", {locale: i18n.dateFnsLocale})}</Text></TouchableOpacity>
            <TimePickerModal
              visible={_isPassageOfTheDayTimePickerVisible}
              hours={passageOfTheDayHours}
              minutes={passageOfTheDayMinutes}
              onConfirm={({hours, minutes}) => {
                dispatch(updateNotificationPreferences({passage_of_the_day_time: `${hours}:${minutes}`}));
                setIsPassageOfTheDayTimePickerVisible(false);
              }}
              onDismiss={() => setIsPassageOfTheDayTimePickerVisible(false)}
            />
          </TouchableOpacity>
        </TypeItem>

        {/* @TODO: do this in next release
        <TypeItem
          title='Due dates'// @TODO: i18n
          description='Reminders for assignments with upcoming deadlines'// @TODO: i18n
          pushStatus={!!notificationPreferences.deadline_reminders_channels.push}
          emailStatus={!!notificationPreferences.deadline_reminders_channels.email}
          pushToggle={() => {
            const currOn = notificationPreferences.deadline_reminders_channels.push;
            dispatch(updateNotificationPreferences({deadline_reminders_on_push: +!currOn}));
          }}
          emailToggle={() => {
            const currOn = notificationPreferences.deadline_reminders_channels.email;
            dispatch(updateNotificationPreferences({deadline_reminders_on_email: +!currOn}));
          }}
        >
          <TouchableOpacity
            // @TODO: style={styles.versesWrapper}
            // @TODO: disabled={true} // to prevent presses from activating the list item onPress
          >
            <Text>TODO: how many days before deadline? 0-7</Text>
          </TouchableOpacity>
        </TypeItem>
        */}

        <TypeItem
          title={i18n('friends')}
          description={i18n('friendsNotificationDescription')}
          pushStatus={!!notificationPreferences.friends_channels.push}
          emailStatus={!!notificationPreferences.friends_channels.email}
          pushToggle={() => {
            const currOn = notificationPreferences.friends_channels.push;
            dispatch(updateNotificationPreferences({friends_on_push: +!currOn}));
          }}
          emailToggle={() => {
            const currOn = notificationPreferences.friends_channels.email;
            dispatch(updateNotificationPreferences({friends_on_email: +!currOn}));
          }}
        />

        <TypeItem
          title={i18n('whatsNewUpdates')}
          description={i18n('whatsNewUpdatesDescriptions')}
          pushStatus={!!notificationPreferences.whats_new_channels.push}
          emailStatus={!!notificationPreferences.whats_new_channels.email}
          pushToggle={() => {
            const currOn = notificationPreferences.whats_new_channels.push;
            dispatch(updateNotificationPreferences({whats_new_on_push: +!currOn}));
          }}
          emailToggle={() => {
            const currOn = notificationPreferences.whats_new_channels.email;
            dispatch(updateNotificationPreferences({whats_new_on_email: +!currOn}));
          }}
        />
      </FullScreenScrollView>
    </ScreenWrapper>
  );
};

export default Notifications;
